import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { dashboardAPI, assignmentAPI, submissionAPI } from '@/utils/api';
import { formatCurrency, formatDate, getCurrentMonth } from '@/utils/formatters';
import { 
  ClipboardList, 
  Receipt, 
  TrendingUp, 
  AlertCircle,
  CheckCircle,
  Clock,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const TranslatorDashboard = () => {
  const { user } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [recentAssignments, setRecentAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [statsRes, assignmentsRes, submissionsRes] = await Promise.all([
        dashboardAPI.getStats(),
        assignmentAPI.list({ status: 'assigned' }),
        submissionAPI.list()
      ]);
      
      setStats(statsRes.data);
      setRecentAssignments(assignmentsRes.data.slice(0, 5));
      setSubmissions(submissionsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmitMonth = async () => {
    const month = getCurrentMonth();
    try {
      await submissionAPI.submit(month);
      toast.success('Month submitted for approval!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };
  
  const currentMonthSubmission = submissions.find(s => s.month === getCurrentMonth());
  
  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold text-primary mb-2">Welcome back, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-muted-foreground">Here's your work overview</p>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-accent hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pending Tasks</CardTitle>
              <ClipboardList className="w-5 h-5 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{stats?.pending_assignments || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Videos to complete</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-creative hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Assignments</CardTitle>
              <FileText className="w-5 h-5 text-creative" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{stats?.total_assignments || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">All time</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-success hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Invoices</CardTitle>
              <Receipt className="w-5 h-5 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{stats?.total_invoices || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Generated</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-warning hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pending Payment</CardTitle>
              <TrendingUp className="w-5 h-5 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{stats?.pending_payment || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Awaiting payment</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Monthly Submission Alert */}
        {currentMonthSubmission ? (
          <Alert className="border-l-4 border-l-success bg-success/5">
            <CheckCircle className="h-5 w-5 text-success" />
            <AlertDescription className="ml-2">
              <strong>Month Submitted:</strong> Your work for {getCurrentMonth()} has been submitted and is pending admin approval.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="border-l-4 border-l-accent bg-accent/5">
            <AlertCircle className="h-5 w-5 text-accent" />
            <AlertDescription className="ml-2 flex items-center justify-between">
              <span>
                <strong>Ready to submit?</strong> Submit your completed work for {getCurrentMonth()} for admin approval.
              </span>
              <Button 
                onClick={handleSubmitMonth}
                size="sm"
                className="ml-4 bg-accent hover:bg-accent/90"
                data-testid="btn-submit-month"
              >
                Submit Month
              </Button>
            </AlertDescription>
          </Alert>
        )}
        
        {/* Recent Assignments */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-accent" />
              Recent Assignments
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentAssignments.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                  <ClipboardList className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">No pending assignments</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentAssignments.map((assignment) => (
                  <div 
                    key={assignment.assignment_id}
                    className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-accent transition-colors"
                    data-testid={`assignment-${assignment.assignment_id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-primary">{assignment.video_title}</h4>
                        <Badge variant="outline" className="text-xs">{assignment.task_type}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {assignment.client_name} • {assignment.project_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-primary">{assignment.video_duration} min</p>
                      <p className="text-xs text-muted-foreground">{formatCurrency(assignment.rate_per_minute)}/min</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TranslatorDashboard;
